// FeelCheckBanner — surfaces the most recent pending "how did that
// land?" prompt as a soft inline banner on TodayScreen. The user
// answers Better / Same / Worse with a single tap; the banner
// optimistically dismisses on response so the UI stays snappy even on
// a slow connection. If the POST fails it'll re-appear on next render
// (no toast nag — the data point isn't critical).
//
// Hidden when there are no pending follow-ups, when the user has
// answered the visible one, or when the prompt is older than 24h
// (the server already filters for this; the client does too as a
// belt-and-suspenders safeguard).

import { useState } from 'react';
import { apiPost } from '../../lib/api';
import { isQueuedOffline } from '../../lib/apiErrors';
import { haptics } from '../../lib/haptics';

export interface PendingFollowup {
  id: number;
  doseId: number;
  scheduledAt: string;
  doseTs: string;
  amount: number;
  amountUnit: string;
}

interface FeelCheckBannerProps {
  pending: PendingFollowup | null;
  onAnswered?: () => void;
}

export function FeelCheckBanner({ pending, onAnswered }: FeelCheckBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!pending || dismissed) return null;

  async function answer(response: 'better' | 'same' | 'worse') {
    if (!pending) return;
    setBusy(true);
    setDismissed(true);
    haptics.confirm();
    try {
      await apiPost(`/api/doses/followups/${pending.id}`, { response });
      onAnswered?.();
    } catch (err) {
      if (!isQueuedOffline(err)) {
        // Quiet failure — un-dismiss so user can retry; no toast nag.
        setDismissed(false);
      }
    } finally {
      setBusy(false);
    }
  }

  const time = new Date(pending.doseTs).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div className="feelcheck-banner" role="status">
      <div className="feelcheck-banner-line">
        <strong>How did the {pending.amount} {pending.amountUnit} from {time} land?</strong>
      </div>
      <div className="feelcheck-banner-buttons">
        <button type="button" className="feelcheck-btn" onClick={() => void answer('better')} disabled={busy}>Better</button>
        <button type="button" className="feelcheck-btn" onClick={() => void answer('same')} disabled={busy}>Same</button>
        <button type="button" className="feelcheck-btn" onClick={() => void answer('worse')} disabled={busy}>Worse</button>
        <button
          type="button"
          className="feelcheck-skip"
          onClick={() => setDismissed(true)}
          aria-label="Skip — answer later"
        >
          Skip
        </button>
      </div>
    </div>
  );
}
